import { useRef } from 'react';
import type { GreetingSetting } from '@schwarz/contract';
import {
  buildGreeting,
  greetingWarnings,
  GREETING_MAX,
  GREETING_PLACEHOLDERS,
} from '@schwarz/contract';

import { PlanetSpinner } from '../../ui/BrandLogo.js';
import { when } from '../../ui/when.js';
import styles from './GreetingPanel.module.css';

interface Props {
  /**
   * The agent name as the editor currently holds it, saved or not.
   *
   * Unsaved on purpose: an operator renaming the agent and rewording the greeting
   * in one sitting should see the two together. A preview built from the saved
   * name would show them a sentence nobody is going to hear.
   */
  agentName: string;
  /** The greeting being edited. Owned by the editor, because it saves it. */
  template: string;
  onChange: (template: string) => void;
  /** What the server holds, for the default comparison and the byline. */
  saved: GreetingSetting | undefined;
}

/**
 * Two contacts, chosen because they take different paths.
 *
 * `spokenAddress` branches on whether a salutation is set — honorific plus
 * surname when there is one, the first name alone when there is not — and the
 * second case is the one people forget exists. Both are rendered at once rather
 * than behind a picker, so the branch is visible without anyone thinking to look
 * for it.
 *
 * The first is Иванов on purpose. That is the surname the preset's hardcoded
 * greeting used to give every contact, and seeing it here rendered from a real
 * contact rather than baked into a template is the whole point of the change.
 */
const SAMPLES = [
  { label: 'With a salutation', name: 'Иван Иванов', salutation: 'MR' as const },
  { label: 'Without one', name: 'Мария Петрова', salutation: 'NONE' as const },
];

/**
 * The line the agent opens a call with (S05).
 *
 * ## Why this is here and not on the Sirma agent
 *
 * The platform preset carries its own first message, and that message greeted
 * every contact as «господин Иванов» — one demo contact's surname, spoken
 * verbatim before any reasoning happened. So the console sends `first_message`
 * per call. The wording is therefore ours, which is why it reaches a different
 * endpoint than the agent does, even though one button saves both.
 *
 * ## Why there is a preview at all
 *
 * Because this field is two placeholders in a sentence, and both fail silently.
 * Delete `{contact}` and every contact is greeted identically; replace `{agent}`
 * with a name typed by hand and the agent-name field above becomes decorative —
 * which is precisely the split that made the greeting say Елена while the agent
 * was called Ева. Neither mistake is visible in the text being typed. Both are
 * obvious in a rendered line.
 */
export function GreetingPanel({ agentName, template, onChange, saved }: Props): JSX.Element {
  const areaRef = useRef<HTMLTextAreaElement>(null);

  if (saved === undefined) {
    return (
      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <h3>Greeting</h3>
        </div>
        <div className={styles.body}>
          <span className={styles.loading}>
            <PlanetSpinner maskId="ps-mark-greeting" />
            Loading the greeting…
          </span>
        </div>
      </div>
    );
  }

  const warnings = greetingWarnings(template);
  const isDefaultWording = template.trim() === saved.defaultTemplate.trim();

  /** Insert a placeholder at the cursor, replacing any selection. */
  const insert = (token: string): void => {
    const element = areaRef.current;

    if (element === null) {
      onChange(template + token);
      return;
    }

    const start = element.selectionStart;
    const end = element.selectionEnd;
    onChange(template.slice(0, start) + token + template.slice(end));

    const caret = start + token.length;
    requestAnimationFrame(() => {
      element.focus();
      element.setSelectionRange(caret, caret);
    });
  };

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <h3>Greeting</h3>
        <span className={styles.sub}>The first thing a contact hears</span>
      </div>

      <div className={styles.body}>
        <p className={styles.note}>
          Spoken before the agent reasons about anything, so it is sent with each call rather than
          stored on the agent. Changes apply from the next call.
        </p>

        <div className={styles.chips}>
          <span className={styles.chipsLabel}>Insert</span>
          {GREETING_PLACEHOLDERS.map((placeholder) => (
            <button
              key={placeholder.token}
              type="button"
              className={styles.chip}
              title={placeholder.describes}
              onClick={() => insert(placeholder.token)}
            >
              {placeholder.label}
            </button>
          ))}
        </div>

        <textarea
          ref={areaRef}
          className={styles.area}
          value={template}
          rows={3}
          maxLength={GREETING_MAX}
          aria-label="Greeting"
          onChange={(event) => onChange(event.target.value)}
        />

        <div className={styles.countRow}>
          <span className={styles.count}>
            {template.length}/{GREETING_MAX}
          </span>
        </div>

        {/* Advisory, never blocking: an impersonal greeting is a legitimate
            choice, and only the operator can say whether it was intended. */}
        {warnings.length > 0 && (
          <div className={styles.warn}>
            <h4>Worth checking</h4>
            <ul>
              {warnings.map((warning) => (
                <li key={warning.token}>{warning.message}</li>
              ))}
            </ul>
          </div>
        )}

        <div className={styles.preview}>
          <h4>What the contact hears</h4>
          {SAMPLES.map((sample) => (
            <div key={sample.label} className={styles.previewRow}>
              <span className={styles.previewLabel}>{sample.label}</span>
              {/* The same function the dialler calls, from the shared package —
                  so a preview that matches is not a coincidence that could
                  quietly stop being true. */}
              <q className={styles.previewLine}>
                {buildGreeting(sample.name, sample.salutation, template, agentName)}
              </q>
            </div>
          ))}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.reset}
            disabled={isDefaultWording}
            onClick={() => onChange(saved.defaultTemplate)}
          >
            Restore the default
          </button>

          {/* Bottom right, and compared on the wording rather than on
              `isDefault`, which only says whether a row exists. A seeded row
              holding the default wording is nobody's choice, and calling it
              "reworded" would send an operator looking for the colleague who
              wrote it. */}
          <span className={styles.byline}>
            {saved.template.trim() === saved.defaultTemplate.trim()
              ? 'This is the default wording.'
              : `Last updated${saved.updatedBy === null ? '' : ` by ${saved.updatedBy}`}${
                  saved.updatedAt === null ? '' : ` · ${when(saved.updatedAt)}`
                }`}
          </span>
        </div>
      </div>
    </div>
  );
}
